import React, { useEffect, useRef, useState } from 'react';
import api, { API_BASE } from '../api';
import { useAuth } from '../context/AuthContext';

const API_ORIGIN = API_BASE.replace(/\/api$/, '');

export default function MyProfile() {
  const { user, refreshMe } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [haddress, setHaddress] = useState(user?.haddress || '');
  const [specialty, setSpecialty] = useState(user?.specialty || '');
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [profileError, setProfileError] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [profileSubmitting, setProfileSubmitting] = useState(false);

  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const photoInputRef = useRef(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwMessage, setPwMessage] = useState('');
  const [pwSubmitting, setPwSubmitting] = useState(false);

  useEffect(() => {
    api.get('/me/specialty-options').then((res) => setSpecialtyOptions(res.data));
  }, []);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileMessage('');

    if (!name.trim()) {
      setProfileError('이름을 입력해 주세요.');
      return;
    }

    setProfileSubmitting(true);
    try {
      await api.put('/me', { name: name.trim(), phone, haddress, specialty: specialty || null });
      await refreshMe();
      setProfileMessage('개인정보가 저장되었습니다.');
    } catch (err) {
      setProfileError(err.response?.data?.detail || '개인정보 저장에 실패했습니다.');
    } finally {
      setProfileSubmitting(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoError('');
    setPhotoUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post('/me/avatar/photo', form);
      await refreshMe();
    } catch (err) {
      setPhotoError(err.response?.data?.detail || '사진 업로드에 실패했습니다.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const removePhoto = async () => {
    setPhotoError('');
    try {
      await api.delete('/me/avatar/photo');
      await refreshMe();
    } catch (err) {
      setPhotoError(err.response?.data?.detail || '사진 삭제에 실패했습니다.');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwMessage('');

    if (newPassword !== confirmPassword) {
      setPwError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (newPassword.length < 8) {
      setPwError('새 비밀번호는 8자 이상이어야 합니다.');
      return;
    }

    setPwSubmitting(true);
    try {
      await api.post('/me/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPwMessage('비밀번호가 변경되었습니다.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwError(err.response?.data?.detail || '비밀번호 변경에 실패했습니다.');
    } finally {
      setPwSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="card">
        <h2>개인정보 수정</h2>

        <label className="field-label">프로필 사진</label>
        <div className="profile-photo-row">
          <div className="profile-photo-preview">
            {user?.avatar_photo_url ? (
              <img src={`${API_ORIGIN}${user.avatar_photo_url}`} alt="" className="profile-photo-img" />
            ) : (
              <span className="profile-photo-fallback">{user?.avatar || '🙂'}</span>
            )}
          </div>
          <div className="profile-photo-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => photoInputRef.current?.click()}
              disabled={photoUploading}
            >
              {photoUploading ? '업로드 중...' : '📷 사진 올리기'}
            </button>
            {user?.avatar_photo_url && (
              <button type="button" className="btn-ghost" onClick={removePhoto}>
                사진 삭제
              </button>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
            />
          </div>
        </div>
        {photoError && <div className="login-error">{photoError}</div>}

        <form onSubmit={handleProfileSubmit}>
          <label className="field-label">이메일 (로그인 ID)</label>
          <input className="field-input" type="email" value={user?.email || ''} disabled />

          <label className="field-label">이름</label>
          <input
            className="field-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <label className="field-label">담당</label>
          <select className="field-input" value={specialty} onChange={(e) => setSpecialty(e.target.value)}>
            <option value="">선택 안 함</option>
            {specialtyOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>

          <label className="field-label">연락처</label>
          <input
            className="field-input"
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="010-0000-0000"
          />

          <label className="field-label">주소</label>
          <input
            className="field-input"
            type="text"
            value={haddress}
            onChange={(e) => setHaddress(e.target.value)}
          />

          {profileError && <div className="login-error">{profileError}</div>}
          {profileMessage && <div className="form-success">{profileMessage}</div>}

          <button className="btn-primary" type="submit" disabled={profileSubmitting}>
            {profileSubmitting ? '저장 중...' : '개인정보 저장'}
          </button>
        </form>
      </div>

      <div className="card">
        <h2>비밀번호 변경</h2>
        <form onSubmit={handlePasswordSubmit}>
          <label className="field-label">현재 비밀번호</label>
          <input
            className="field-input"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />

          <label className="field-label">새 비밀번호</label>
          <input
            className="field-input"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="8자 이상"
            required
          />

          <label className="field-label">새 비밀번호 확인</label>
          <input
            className="field-input"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />

          {pwError && <div className="login-error">{pwError}</div>}
          {pwMessage && <div className="form-success">{pwMessage}</div>}

          <button className="btn-primary" type="submit" disabled={pwSubmitting}>
            {pwSubmitting ? '변경 중...' : '비밀번호 변경'}
          </button>
        </form>
      </div>
    </div>
  );
}
